import { useState, useRef, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { ChevronDown, Check, Building2, Plus } from 'lucide-react';
import { brand } from '@iheart/shared';
import { cn } from '@iheart/ui';
import { motion } from 'framer-motion';
import { useWorkspaces, useCurrentWorkspace, useWorkspaceStore, type Workspace } from '../lib/workspace';
import { useConfirm } from './ConfirmDialog';
import { RailTooltip } from './RailTooltip';

/**
 * Workspace switcher dropdown.
 * Desktop: compact icon + name in the sidebar rail area.
 * Mobile: full-width selector that can be embedded in the More overlay.
 *
 * Always visible — shows current workspace initials (with brand color)
 * even when there's only one workspace, so the user can see which
 * workspace they're in and access the "New workspace" shortcut.
 */
export function WorkspaceSwitcher({ variant = 'desktop' }: { variant?: 'desktop' | 'mobile' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { data: workspaces } = useWorkspaces();
  const current = useCurrentWorkspace();
  const setCurrent = useWorkspaceStore((s) => s.setCurrent);

  const handleSwitch = async (ws: Workspace) => {
    if (current?.publicId === ws.publicId) { setOpen(false); return; }
    const ok = await confirm({
      title: 'Are you sure you want to switch?',
      message: `You are about to switch from "${current?.name}" to "${ws.name}".`,
      confirmLabel: 'Yes, switch',
      cancelLabel: 'No, stay here',
      variant: 'switch',
    });
    if (ok) {
      setCurrent(ws);
      setOpen(false);
    }
  };

  // Close on outside click or Escape key
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClick);
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  if (!workspaces || !current) return null;

  const initials = current.name
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const handleNewWorkspace = () => {
    setOpen(false);
    navigate('/settings/workspace?new=true');
  };

  if (variant === 'mobile') {
    return (
      <div ref={ref} className="relative">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-label="Switch workspace"
          aria-expanded={open}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-neutral-600 hover:bg-muted"
        >
          <span
            className="flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-md text-[9px] font-semibold text-white"
            style={{ backgroundColor: current.logoUrl ? undefined : (current.brandColor ?? '#a3a3a3') }}
          >
            {current.logoUrl ? (
              <img src={current.logoUrl} alt={current.name} className="size-full object-cover" />
            ) : initials}
          </span>
          <span className="flex-1 truncate text-left font-medium">{current.name}</span>
          <ChevronDown className={cn('size-4 text-neutral-400 transition-transform', open && 'rotate-180')} />
        </button>
        {open && (
          <div className="absolute left-0 top-full z-50 mt-1 max-h-60 min-w-[220px] overflow-y-auto rounded-lg border border-white/30 bg-white/90 py-1 shadow-lg backdrop-blur-2xl shadow-[0_4px_30px_rgba(0,0,0,0.05)]">
            {workspaces.map((ws) => (
              <WorkspaceOption
                key={ws.publicId}
                workspace={ws}
                isActive={current.publicId === ws.publicId}
                onSelect={() => void handleSwitch(ws)}
              />
            ))}
            <div className="my-1 border-t border-neutral-100" />
            <button
              type="button"
              onClick={handleNewWorkspace}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-neutral-500 transition-colors hover:bg-neutral-50 hover:text-neutral-700"
            >
              <Plus className="size-4" />
              <span>New workspace</span>
            </button>
          </div>
        )}
      </div>
    );
  }

  // Desktop compact variant
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        title={current.name}
        aria-label={`Switch workspace (${current.name})`}
        aria-expanded={open}
        className="flex size-10 items-center justify-center overflow-hidden rounded-lg text-[11px] font-semibold transition-colors hover:bg-muted"
        style={{
          backgroundColor: current.logoUrl ? undefined : (current.brandColor ?? undefined),
          color: !current.logoUrl && current.brandColor ? '#ffffff' : undefined,
        }}
      >
        {current.logoUrl ? (
          <img src={current.logoUrl} alt={current.name} className="size-full object-cover" />
        ) : current.brandColor ? (
          initials
        ) : (
          <Building2 className="size-5 text-neutral-500" />
        )}
      </button>
      {open && (
        <div className="absolute left-12 top-0 z-50 w-56 rounded-xl border border-white/30 bg-white/80 py-1 shadow-xl backdrop-blur-2xl shadow-[0_4px_30px_rgba(0,0,0,0.05)]">
          <div className="px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider text-neutral-400">
            Workspaces
          </div>
          <div className="max-h-72 overflow-y-auto">
            {workspaces.map((ws) => (
              <WorkspaceOption
                key={ws.publicId}
                workspace={ws}
                isActive={current.publicId === ws.publicId}
                onSelect={() => void handleSwitch(ws)}
              />
            ))}
          </div>
          <div className="my-1 border-t border-neutral-100" />
          <button
            type="button"
            onClick={handleNewWorkspace}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-neutral-500 transition-colors hover:bg-neutral-50 hover:text-neutral-700"
          >
            <Plus className="size-4" />
            <span>New workspace</span>
          </button>
        </div>
      )}
    </div>
  );
}

function WorkspaceOption({
  workspace,
  isActive,
  onSelect,
}: {
  workspace: Workspace;
  isActive: boolean;
  onSelect: () => void;
}) {
  const wsInitials = workspace.name
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors',
        isActive ? 'bg-pink-50 text-pink-700' : 'text-neutral-700 hover:bg-neutral-50',
      )}
    >
      <span
        className="flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-md text-[10px] font-semibold text-white"
        style={{
          backgroundColor: workspace.logoUrl ? undefined : (workspace.brandColor ?? '#a3a3a3'),
        }}
      >
        {workspace.logoUrl ? (
          <img src={workspace.logoUrl} alt={workspace.name} className="size-full object-cover" />
        ) : wsInitials}
      </span>
      <span className="flex-1 truncate font-medium">{workspace.name}</span>
      {isActive && <Check className="size-4 shrink-0 text-pink-600" />}
    </button>
  );
}

/**
 * Sidebar nav link that navigates to the /workspace hub page.
 * Shows the current workspace initials with brand color, matching
 * the RailLink visual style. Tooltip shows the workspace name.
 */
export function WorkspaceNavLink() {
  const { data: workspaces } = useWorkspaces();
  const current = useCurrentWorkspace();

  if (!workspaces || !current) {
    return (
      <div className="flex size-10 items-center justify-center rounded-lg text-neutral-400">
        <Building2 className="size-5" />
      </div>
    );
  }

  const initials = current.name
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <RailTooltip label={current.name}>
      <NavLink to="/workspace" className="block">
        {({ isActive }) => (
          <motion.span
            whileHover={{ scale: brand.motion.grow }}
            whileTap={{ scale: brand.motion.grow }}
            transition={brand.motion.spring}
            className={cn(
              'flex size-10 items-center justify-center rounded-lg text-[11px] font-semibold transition-colors overflow-hidden',
              isActive
                ? 'text-white ring-2 ring-pink-400/50'
                : 'hover:bg-muted',
            )}
            style={{
              backgroundColor: current.logoUrl ? undefined : isActive
                ? (current.brandColor ?? '#a3a3a3')
                : (current.brandColor ?? undefined),
              color: !current.logoUrl && (current.brandColor || isActive) ? '#ffffff' : undefined,
            }}
          >
            {current.logoUrl ? (
              <img src={current.logoUrl} alt={current.name} className="size-full object-cover" />
            ) : current.brandColor ? (
              initials
            ) : isActive ? (
              initials
            ) : (
              <Building2 className="size-5 text-neutral-500" />
            )}
          </motion.span>
        )}
      </NavLink>
    </RailTooltip>
  );
}
